<template>
  <DonationAmount :donation_amount="state.donation_amount"></DonationAmount>
  <div class="bg-white p-4 form-cont m-auto rounded-3 mb-5 dir-ltr">
    <form
      :action="state.redirectURI"
      class="paymentWidgets"
      :data-brands="dataBrands"
    ></form>
  </div>

  <DonationSuccessDialog
    :showModal="state.showDonationSuccessModal"
    :data="state.successDonationData"
    :heading="state.paymentSuccessHeading"
    :message="state.paymentSuccessMessage"
    :donationType="state.donationType"
    v-if="state.showDonationSuccessModal"
    @close="state.showDonationSuccessModal = false"
    :sehatty="state.isSehatty"
  />

  <DonationFailureDialog
    :showModal="state.showDonationFailureModal"
    :message="state.paymentFailureMessage"
    :heading="state.paymentFailureHeading"
    :donationType="state.donationType"
    v-if="state.showDonationFailureModal"
    @close="state.showDonationFailureModal = false"
    :sehatty="state.isSehatty"
  />

  <DonationInProgressDialog
    :showModal="state.showInProgressModal"
    :data="state.progessDonationData"
    :status="state.progressStatus"
    :donationType="state.donationType"
    v-if="state.showInProgressModal"
    @close="state.showInProgressModal = false"
  />

  <GiftCardSuccessDialog
    :showModal="state.showGiftCardSuccessModal"
    :message="state.giftSuccessMessage"
    :heading="state.giftSuccessHeading"
    :data="state.successGiftCardData"
    v-if="state.showGiftCardSuccessModal"
    @close="state.showGiftCardSuccessModal = false"
  />
</template>

<script setup>
import { reactive, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { defineAsyncComponent } from "vue";
import { useHyperpay } from "@/composables/useHyperpay";
import { DONATION_TYPE } from "@/services/enum.service";
import {
  getLocalValue,
  setLocalValue,
  removeLocalValue,
} from "@/hooks/useLocalStorage";

import DonationService from "@/services/donation.service";
import DonationSuccessDialog from "@/components/donate/DonationSuccessDialog";
import DonationFailureDialog from "@/components/donate/DonationFailureDialog";
import GiftCardSuccessDialog from "@/views/gift-card/GiftCardSuccessDialog";
import DonationAmount from "@/components/donate/DonationAmount";
import { useStore } from "vuex";
import { extractPaymentFailureMessage } from "@/helpers/paymentStatusError";

const DonationInProgressDialog = defineAsyncComponent(() =>
  import("@/components/donate/DonationInProgressDialog.vue")
);

const route = useRoute();
const store = useStore();
const state = reactive({
  cardType: "credit_card",
  redirectURI: "",
  showDonationSuccessModal: false,
  successDonationData: {},
  progessDonationData: {},
  progressStatus: 202,
  successGiftCardData: {},
  paymentSuccessHeading: '',
  paymentSuccessMessage: "",
  donationType: DONATION_TYPE.CASE,
  paymentFailureMessage: "",
  paymentFailureHeading: '',
  showDonationFailureModal: false,
  showInProgressModal: false,
  showGiftCardSuccessModal: false,
  giftSuccessHeading: "",
  giftSuccessMessage: "",
  resourcePath: "",
  paymentTransactionId: 0,
  donation_amount: null,
  isAmbassador: false,
  referenceId: null,
  giftPaymentTransactionId: null,
  integrity: getLocalValue("scriptIntegrity"),
  isSehatty: store.getters.isSehattyPage,
});

const { dataBrands } = useHyperpay(state.cardType);

const setDonationAmount = () => {
  if (route.query.donation_amount) {
    state.donation_amount = route.query.donation_amount;
  }
};

const handleGiftCardRedirect = async () => {
  if (route.query.id && route.query.resourcePath) {
    try {
        const { data } = await DonationService.getPaymentStatusWithRetry(
        route.query.id,
        route.query.resourcePath
      );
      setLocalValue("paymentStatus", data.status);
      if (data.status === 200) {
        if (data.gift_card_success_data) {
          const giftCardData = {
                gift_card_success_data: data.gift_card_success_data,
                message: data.message,
                heading: data.heading,
          };
          setGiftCardSuccessData(giftCardData);
          setLocalValue("giftPaymentId", state.giftPaymentTransactionId);
          setLocalValue(state.giftPaymentTransactionId, giftCardData);

          // cleanup for giftModel
          removeLocalValue("giftModel");
          removeLocalValue("wallet");
        }
      } else if (data.status === 202 || data.status ===400){
        setDonationProgressData(data.result, data.status);
      }
    } catch (error) {
       state.paymentFailureMessage = extractPaymentFailureMessage(error);
      if (error?.response?.data?.errors?.length > 0) {
        state.paymentFailureHeading = error.response.data.heading;
      }
      state.showDonationFailureModal = true;
    }
  }
};

const setDonationSuccessData = (data) => {
  if (!data) return;
  state.successDonationData = data?.donation_success_data;
  state.paymentSuccessMessage = data?.message;
  state.paymentSuccessHeading = data?.heading;
  state.donationType = data?.donation_success_data?.type || DONATION_TYPE.CASE;
  state.showDonationSuccessModal = true;
};

const setDonationProgressData = (data, status) => {
  if (!data) return;
  state.progessDonationData = data;
  state.donationType = data.donation_info?.type || DONATION_TYPE.CASE;
  state.progressStatus = status || 202;
  state.showInProgressModal = true;
};

const setGiftCardSuccessData = (data) => {
  state.successGiftCardData = data.gift_card_success_data;
  state.giftSuccessMessage = data.message;
  state.giftSuccessHeading = data.heading;
  state.showGiftCardSuccessModal = true;
};

const checkPaymentStatus = async () => {
  const isPaymentExist = getLocalValue(state.paymentTransactionId.toString());
  if (isPaymentExist) {
    if (getLocalValue("giftPaymentId") === state.paymentTransactionId) {
      if (getLocalValue("paymentStatus") === 200) {
        setGiftCardSuccessData(isPaymentExist);
      } else if (getLocalValue("paymentStatus") === 202 || getLocalValue("paymentStatus") === 400) {
        setDonationProgressData(isPaymentExist, getLocalValue("paymentStatus"));
      }
    } else {
      if (getLocalValue("paymentStatus") === 202 || getLocalValue("paymentStatus") === 400) {
        setDonationProgressData(isPaymentExist, getLocalValue("paymentStatus"));
        state.donationType = isPaymentExist.donation_info?.type;
      } else if (getLocalValue("paymentStatus") === 200) {
        setDonationSuccessData(isPaymentExist);
      }
    }
    return;
  }

  try {
      const { data } = await DonationService.getPaymentStatusWithRetry(
      state.paymentTransactionId,
      state.resourcePath
    );

    setLocalValue("paymentStatus", data.status);

    if ((data.status === 202 || data.status ===400) && data.result) {
      setDonationProgressData(data.result, data.status);
      setLocalValue("paymentId", state.paymentTransactionId);
      setLocalValue(state.paymentTransactionId, state.progessDonationData);
    } else if (data.status === 200 && data.donation_success_data) {
      const successData = {
        donation_success_data: data.donation_success_data,
        message: data.message,
        heading: data.heading
      };
      setDonationSuccessData(successData);
      setLocalValue("paymentId", state.paymentTransactionId);
      setLocalValue(state.paymentTransactionId, successData);
    }

    removeLocalValue("donationModel");
    removeLocalValue("wallet");
  } catch (error) {
     state.paymentFailureMessage = extractPaymentFailureMessage(error);
    if (error?.response?.data?.errors?.length > 0) {
      state.paymentFailureHeading = error.response.data.heading;
    }
    state.showDonationFailureModal = true;
  }
};

onMounted(async () => {
  setDonationAmount();

  state.cardType = route.query.card_type;
  if (route.query.reference_id) {
    state.isAmbassador = true;
    state.referenceId = route.query.reference_id;
  }

  state.redirectURI = process.env.VUE_APP_FE_URL + `/checkout`;
  const queryParams = [];
  if(state.isSehatty) {
    queryParams.push('sehatty=true');
  }
  if(route.query.mode) {
    queryParams.push(`mode=${route.query.mode}`);
  }
  if(queryParams.length > 0) {
    state.redirectURI += `?${queryParams.join('&')}`;
  }

  if (getLocalValue("gift-card-payment")) {
    state.giftPaymentTransactionId = route.query.id;
    await handleGiftCardRedirect();
    return;
  }

  if (route.query.payment_type === "gift_card") {
    setLocalValue("gift-card-payment", true);
  }

  if (route.query.id && route.query.resourcePath) {
    state.resourcePath = route.query.resourcePath;
    state.paymentTransactionId = route.query.id;
    checkPaymentStatus();
  } else {
    setLocalValue("linkOfDonatedObject", window.history.state.back);
  }
});

onUnmounted(() => {
  const getLatestPayment = getLocalValue("paymentId");
  if (getLatestPayment) {
    removeLocalValue("paymentId");
    removeLocalValue(getLatestPayment.toString());
    removeLocalValue("paymentStatus");
  }
  const getLatestGiftPayment = getLocalValue("giftPaymentId");
  if (getLatestGiftPayment) {
    removeLocalValue("giftPaymentId");
    removeLocalValue(getLatestGiftPayment.toString());
  }
  const isGiftCardPayment = getLocalValue("gift-card-payment");
  if (isGiftCardPayment){
    removeLocalValue("gift-card-payment");
    removeLocalValue('giftModel')
  }
});
</script>

<style lang="scss" scoped>
.form-cont {
  width: fit-content;
}

.dir-ltr {
  direction: ltr;
}
</style>
